import { callGateway, type CallerIdentity } from "./gateway";
import { config } from "../config";

/**
 * The operator's public contact and guest-facing legal documents, held by
 * radaccess (v1.51): what the captive portal's footer and its Legal & Terms
 * pages show. Design: radius-wiki portal-brand-from-logo.md, "Page background,
 * layout, contact and legal pages" §2 and §3.
 *
 * 🔑 A DIFFERENT GRANT FROM api/operator.ts. Profile, tax, licence, bank and the
 * registered (invoice) address are `operator:*`, superadmin only. Everything
 * here is the `operatorcontact` resource -- public by nature -- so an Operator
 * may write it. The contact is stored as rows typed `portal`; the registered
 * address an invoice prints is never touched from here.
 */

const PREFIX = "/operator";
const enc = (s: string) => encodeURIComponent(s);

/** The contact as the portal shows it: one address, one phone (country code included), one email, the website. */
export interface PublicContact {
  address?: string;
  city?: string;
  state?: string;
  pin?: string;
  country?: string;
  phone?: string;
  email?: string;
  website?: string;
}

/** What the contact form edits and PUT /publiccontact takes. An empty group removes that line. */
export interface ContactDraft {
  address: string;
  city: string;
  state: string;
  pin: string;
  country: string;
  countrycode: string;
  phone: string;
  email: string;
  website: string;
}

export const EMPTY_CONTACT: ContactDraft = {
  address: "", city: "", state: "", pin: "", country: "India", countrycode: "+91", phone: "", email: "", website: "",
};

/** The form's starting values from what guests see today. radaccess sends the phone as country code + number. */
export function draftFromContact(c: PublicContact | null | undefined): ContactDraft {
  const d: ContactDraft = { ...EMPTY_CONTACT };
  if (!c) return d;
  for (const f of ["address", "city", "state", "pin", "country", "email", "website"] as const) {
    if (c[f]) d[f] = c[f]!;
  }
  const raw = (c.phone ?? "").replace(/[\s-]/g, "");
  const m = /^(\+\d{1,4})(\d{10})$/.exec(raw);
  if (m) {
    d.countrycode = m[1]!;
    d.phone = m[2]!;
  } else if (raw) {
    d.phone = raw.replace(/^\+/, "");
  }
  return d;
}

/** Any C0 control character or DEL -- the same test radaccess applies. */
function hasControl(v: string): boolean {
  for (const ch of v) {
    const c = ch.codePointAt(0)!;
    if (c < 0x20 || c === 0x7f) return true;
  }
  return false;
}
const EMAIL = /^[^\s@<>(),;:"]+@[^\s@<>(),;:"]+\.[^\s@<>(),;:"]+$/;

/** Field -> why it cannot be saved. The same rules radaccess applies; the server stays the authority. */
export function contactProblems(d: ContactDraft): Partial<Record<keyof ContactDraft, string>> {
  const out: Partial<Record<keyof ContactDraft, string>> = {};
  const max: Array<[keyof ContactDraft, number]> = [["address", 500], ["city", 100], ["state", 100], ["pin", 20], ["country", 100], ["email", 255], ["website", 2048]];
  for (const [f, n] of max) {
    const v = d[f].trim();
    if ([...v].length > n) out[f] = `At most ${n} characters.`;
    else if (hasControl(v)) out[f] = "Contains a control character.";
  }
  const phone = d.phone.replace(/[\s-]/g, "");
  if (phone && !/^\d{6,15}$/.test(phone)) out.phone = "Digits only, 6 to 15 of them.";
  const code = d.countrycode.trim();
  if (phone && code && !/^\+\d{1,4}$/.test(code)) out.countrycode = "Like +91.";
  if (d.email.trim() && !out.email && !EMAIL.test(d.email.trim())) out.email = "Not an email address.";
  const w = d.website.trim();
  if (w && !out.website) {
    let ok = false;
    try {
      const u = new URL(w);
      ok = u.protocol === "https:" && Boolean(u.hostname) && !/\s/.test(w);
    } catch {
      ok = false;
    }
    if (!ok) out.website = "A full address starting with https://";
  }
  return out;
}

/** The body PUT sends: trimmed, the phone as digits. */
export function contactBody(d: ContactDraft): ContactDraft {
  const phone = d.phone.replace(/[\s-]/g, "");
  return {
    address: d.address.trim(), city: d.city.trim(), state: d.state.trim(), pin: d.pin.trim(), country: d.country.trim(),
    countrycode: phone ? d.countrycode.trim() || "+91" : "", phone, email: d.email.trim(), website: d.website.trim(),
  };
}

export const getPublicContact = (i: CallerIdentity, ns: string) =>
  callGateway<PublicContact>(i, config().gatewayBase, `${PREFIX}/${enc(ns)}/publiccontact`);

export const savePublicContact = (i: CallerIdentity, ns: string, d: ContactDraft) =>
  callGateway<PublicContact>(i, config().gatewayBase, `${PREFIX}/${enc(ns)}/publiccontact`, { method: "PUT", body: contactBody(d) });

export const DOCUMENT_KINDS = ["pricing", "terms", "privacy", "refunds"] as const;
export type DocumentKind = (typeof DOCUMENT_KINDS)[number];

export const DOCUMENT_TITLES: Record<DocumentKind, string> = {
  pricing: "Pricing note",
  terms: "Terms of Service",
  privacy: "Privacy Policy",
  refunds: "Cancellation & Refunds",
};

export interface OperatorDocument {
  kind: DocumentKind;
  body: string;
  /** Where the document is stored: this namespace, or the nearest one above it. */
  namespace: string;
  inherited: boolean;
  updated_at: string;
}

export const MAX_DOCUMENT_CHARS = 20000;

/** CRLF and CR become LF; surrounding whitespace goes. Empty means "no document". */
export const normaliseDocument = (v: string) => v.replace(/\r\n?/g, "\n").trim();

/** Why `body` cannot be saved, or null. The same rules radaccess applies. */
export function documentProblem(body: string): string | null {
  const v = normaliseDocument(body);
  if ([...v].length > MAX_DOCUMENT_CHARS) return `At most ${MAX_DOCUMENT_CHARS} characters.`;
  for (const ch of v) {
    const c = ch.codePointAt(0)!;
    if ((c < 0x20 && c !== 0x0a) || c === 0x7f) return "Contains a control character (a tab, for example).";
  }
  return null;
}

export const listDocuments = (i: CallerIdentity, ns: string) =>
  callGateway<{ documents: OperatorDocument[] }>(i, config().gatewayBase, `${PREFIX}/${enc(ns)}/document`);

/** Saves this namespace's own document; an empty body deletes it (and an inherited one shows again). */
export const saveDocument = (i: CallerIdentity, ns: string, kind: DocumentKind, body: string) =>
  callGateway<OperatorDocument | { deleted: string }>(i, config().gatewayBase, `${PREFIX}/${enc(ns)}/document/${enc(kind)}`, {
    method: "PUT",
    body: { body: normaliseDocument(body) },
  });
